import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Cell, Input, Spinner } from '@telegram-apps/telegram-ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { SORT_LABELS, type SortOption } from '@mass-cult/shared';
import { EmptyState, ErrorState, TitleListItem } from '../../components/ui';
import {
  createFolder,
  deleteFolder,
  getFolderItems,
  getFolders,
  removeFromFolder,
  renameFolder,
} from '../../shared/api/client';

export function FoldersPage() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  const foldersQuery = useQuery({
    queryKey: ['folders'],
    queryFn: getFolders,
  });

  const createMutation = useMutation({
    mutationFn: () => createFolder(name.trim()),
    onSuccess: () => {
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['folders'] });
    },
  });

  return (
    <div>
      <h1>Папки</h1>
      <Input
        header="Название новой папки"
        placeholder="Например, Новогодние"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <Button
        stretched
        style={{ margin: '12px 0' }}
        disabled={!name.trim() || createMutation.isPending}
        onClick={() => createMutation.mutate()}
      >
        Создать папку
      </Button>

      {foldersQuery.isLoading && <Spinner size="m" />}
      {foldersQuery.isError && (
        <ErrorState message={(foldersQuery.error as Error).message} />
      )}
      {!foldersQuery.isLoading && foldersQuery.data?.length === 0 && (
        <EmptyState title="Папок пока нет" />
      )}

      {foldersQuery.data?.map((folder) => (
        <Link
          key={folder.id}
          to={`/folders/${folder.id}`}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <Cell subtitle={`${folder.itemCount} элементов`}>{folder.name}</Cell>
        </Link>
      ))}
    </div>
  );
}

export function FolderDetailPage() {
  const { id = '0' } = useParams();
  const folderId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sort, setSort] = useState<SortOption>('added_desc');
  const [renameValue, setRenameValue] = useState('');

  const foldersQuery = useQuery({
    queryKey: ['folders'],
    queryFn: getFolders,
  });

  const itemsQuery = useQuery({
    queryKey: ['folder-items', folderId, sort],
    queryFn: () => getFolderItems(folderId, sort),
    enabled: !Number.isNaN(folderId),
  });

  const folder = foldersQuery.data?.find((item) => item.id === folderId);

  const renameMutation = useMutation({
    mutationFn: () => renameFolder(folderId, renameValue.trim()),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['folders'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteFolder(folderId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['folders'] });
      navigate('/folders');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (item: { mediaType: string; tmdbId: number }) =>
      removeFromFolder(folderId, item.mediaType, item.tmdbId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['folder-items', folderId] });
      void queryClient.invalidateQueries({ queryKey: ['folders'] });
    },
  });

  return (
    <div>
      <h1>{folder?.name ?? 'Папка'}</h1>

      <Input
        header="Переименовать"
        placeholder={folder?.name ?? 'Название'}
        value={renameValue}
        onChange={(event) => setRenameValue(event.target.value)}
      />
      <div className="toolbar">
        <Button
          mode="bezeled"
          disabled={!renameValue.trim()}
          onClick={() => renameMutation.mutate()}
        >
          Сохранить имя
        </Button>
        <Button mode="gray" onClick={() => deleteMutation.mutate()}>
          Удалить папку
        </Button>
      </div>

      <div className="toolbar">
        <select value={sort} onChange={(event) => setSort(event.target.value as SortOption)}>
          {Object.entries(SORT_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {itemsQuery.isLoading && <Spinner size="m" />}
      {itemsQuery.isError && <ErrorState message={(itemsQuery.error as Error).message} />}
      {!itemsQuery.isLoading && itemsQuery.data?.length === 0 && (
        <EmptyState title="В папке пока пусто" />
      )}

      {itemsQuery.data?.map((item) => (
        <div key={`${item.mediaType}-${item.tmdbId}`}>
          <TitleListItem
            mediaType={item.mediaType}
            tmdbId={item.tmdbId}
            title={item.title}
            subtitle={[item.year, item.rating ? `Оценка ${item.rating}` : null]
              .filter(Boolean)
              .join(' · ')}
            posterUrl={item.posterUrl}
            rating={item.rating}
            status={item.status}
          />
          <Button
            mode="gray"
            stretched
            style={{ marginBottom: 12 }}
            onClick={() =>
              removeMutation.mutate({ mediaType: item.mediaType, tmdbId: item.tmdbId })
            }
          >
            Удалить из папки
          </Button>
        </div>
      ))}
    </div>
  );
}
